// apps/web/src/lib/toast.tsx
// Top-center transient toasts. `const toast = useToast(); toast.success("Saved")`.
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

type ToastType = "success" | "error";
interface Toast { id: number; message: string; type: ToastType }

interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  show: (message: string, type: ToastType) => void;
}

const ToastCtx = createContext<ToastApi | null>(null);
const DURATION_MS = 3000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const remove = useCallback((id: number) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((message: string, type: ToastType) => {
    const id = ++idRef.current;
    setToasts((ts) => [...ts, { id, message, type }]);
    setTimeout(() => remove(id), DURATION_MS);
  }, [remove]);

  const api = useMemo<ToastApi>(() => ({
    success: (m) => show(m, "success"),
    error: (m) => show(m, "error"),
    show,
  }), [show]);

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="fixed top-0 inset-x-0 z-[70] pt-safe px-4 flex flex-col items-center pointer-events-none"
      >
        {toasts.map((tt) => (
          <button
            key={tt.id}
            type="button"
            onClick={() => remove(tt.id)}
            className={`pointer-events-auto mt-2 w-full max-w-sm rounded-2xl bg-elevated px-4 py-3 text-sm font-medium text-fg text-left shadow-card animate-slide-down border ${
              tt.type === "success" ? "border-success/40" : "border-danger/40"
            }`}
          >
            <span aria-hidden="true" className={tt.type === "success" ? "text-success" : "text-danger"}>
              {tt.type === "success" ? "✓" : "✕"}
            </span>{" "}
            {tt.message}
          </button>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
